import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SettingsIcon from '@mui/icons-material/Settings';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import {
  alpha,
  useTheme,
  Avatar,
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Typography,
  ListItemButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useLogout } from 'services/hooks';
import { locations } from 'services/routes/locations';
import YoutubeChannelIcon from 'assets/icons/YouTubePlusIcon';
import { selectSelf } from 'services/store/user/selectors';

const SideBar = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const user = useSelector(selectSelf);

  const logout = useLogout();

  const getInitials = (firstName: string, lastName: string) =>
    (firstName[0] + lastName[0]).toUpperCase();

  const isPageSelected = (page: string) => page === location.pathname;

  const handleLogout = () => {
    logout();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: theme.spacing(43.25),
        position: 'relative',
        top: 0,
      }}
      data-testid="sidebar-container"
    >
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <Box
          sx={{
            height: '100%',
            zIndex: 100,
            flex: 1,
            backgroundColor: theme.palette.grey[800],
            overflowY: 'auto',
          }}
        >
          <List>
            <ListItem
              sx={{
                height: theme.spacing(16),
                paddingLeft: theme.spacing(3),
              }}
            >
              <ListItemAvatar>
                <Avatar
                  data-testid="userInitialsAvatar"
                  sx={{
                    width: theme.spacing(6),
                    height: theme.spacing(6),
                    color: theme.palette.common.white,
                  }}
                >
                  {user.firstName && user.lastName
                    ? getInitials(user.firstName, user.lastName)
                    : null}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                data-testid="userName"
                disableTypography
                sx={{
                  paddingLeft: theme.spacing(3),
                }}
              >
                <Typography noWrap variant="h4">
                  {user.firstName && user.lastName
                    ? `${user.firstName} ${user.lastName}`
                    : `${user.email}`}
                </Typography>
              </ListItemText>
            </ListItem>

            <ListItemButton
              component={Link}
              data-testid="overviewButton"
              to={locations.account.overview.pathname}
              selected={isPageSelected(locations.account.overview.pathname)}
              sx={{
                height: theme.spacing(10),
                paddingLeft: theme.spacing(6),
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  background: theme.palette.grey[900],
                  '&:hover': {
                    background: alpha(theme.palette.common.white, 0.1),
                  },
                },
              }}
            >
              <ListItemIcon>
                <SettingsIcon
                  color={
                    isPageSelected(locations.account.overview.pathname)
                      ? 'primary'
                      : 'inherit'
                  }
                />
              </ListItemIcon>
              <ListItemText
                data-testid="overviewText"
                primary={t('account:sideBar:account')}
              />
            </ListItemButton>

            <ListItemButton
              component={Link}
              data-testid="subscriptionButton"
              to={locations.account.subscription.pathname}
              selected={isPageSelected(locations.account.subscription.pathname)}
              sx={{
                height: theme.spacing(10),
                paddingLeft: theme.spacing(6),
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  background: theme.palette.grey[900],
                  '&:hover': {
                    background: alpha(theme.palette.common.white, 0.1),
                  },
                },
              }}
            >
              <ListItemIcon>
                <SubscriptionsIcon
                  color={
                    isPageSelected(locations.account.subscription.pathname)
                      ? 'primary'
                      : 'inherit'
                  }
                />
              </ListItemIcon>
              <ListItemText
                data-testid="subscriptionText"
                primary={t('account:sideBar:subscription')}
              />
            </ListItemButton>
            <ListItemButton
              component={Link}
              data-testid="youtubeButton"
              to={locations.account.youTube.pathname}
              selected={isPageSelected(locations.account.youTube.pathname)}
              sx={{
                height: theme.spacing(10),
                paddingLeft: theme.spacing(6),
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  background: theme.palette.grey[900],
                  '&:hover': {
                    background: alpha(theme.palette.common.white, 0.1),
                  },
                },
              }}
            >
              <ListItemIcon>
                <YoutubeChannelIcon
                  color={
                    isPageSelected(locations.account.youTube.pathname)
                      ? theme.palette.primary.main
                      : theme.palette.text.primary
                  }
                />
              </ListItemIcon>
              <ListItemText
                data-testid="youTubeText"
                primary={t('account:sideBar:youTubeChannel')}
              />
            </ListItemButton>
            <ListItemButton
              onClick={handleLogout}
              data-testid="logoutButton"
              sx={{
                height: theme.spacing(10),
                paddingLeft: theme.spacing(6),
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                  background: theme.palette.grey[900],
                  '&:hover': {
                    background: alpha(theme.palette.common.white, 0.1),
                  },
                },
              }}
            >
              <ListItemIcon>
                <ExitToAppIcon />
              </ListItemIcon>
              <ListItemText
                data-testid="logoutText"
                primary={t('account:sideBar:logOut')}
              />
            </ListItemButton>
          </List>
        </Box>
      </Box>
    </Box>
  );
};

export default SideBar;
